/**
 * FX — pooled particle & effect engine for Lightning Arena.
 *
 * Design goals: cinematic look, zero per-frame allocations, constant light
 * count (three.js recompiles every material when the number of lights in the
 * scene changes — so flashes use a fixed pool of PointLights kept in the scene).
 *
 *  - Additive billboard pool (glow puffs, velocity-stretched sparks, tracers, motes)
 *  - Alpha-blended smoke pool (soft puffs, grow + fade)
 *  - Debris pool (InstancedMesh gunmetal chunks with gravity + floor bounce)
 *  - Shockwave rings (oriented, expanding, fading)
 *  - 6 pooled PointLights for muzzle flashes / explosions / engines
 *
 * All sprites come from one procedurally generated 2×2 atlas (no asset files).
 */

import * as THREE from 'three';

export type FloorQuery = (x: number, y: number, z: number) => number | null;

const ATLAS = { glow: 0, spark: 1, smoke: 2, disc: 3 } as const;

function buildAtlas(): THREE.CanvasTexture {
  const S = 512, T = 256;
  const c = document.createElement('canvas');
  c.width = S; c.height = S;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, S, S);

  // tile 0 (glow) — top-left (flipY=false → row 0 = canvas top)
  let g = ctx.createRadialGradient(T / 2, T / 2, 0, T / 2, T / 2, T / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.18, 'rgba(255,255,255,0.85)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.28)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, T, T);

  // tile 1 (spark streak) — top-right: bright horizontal streak, soft ends
  const img = ctx.createImageData(T, T);
  for (let y = 0; y < T; y++) {
    const fy = (y / T) * 2 - 1;
    for (let x = 0; x < T; x++) {
      const fx = (x / T) * 2 - 1;
      const core = Math.max(0, 1 - Math.abs(fy) * 3.2);
      const halo = Math.max(0, 1 - Math.abs(fy) * 1.2) * 0.35;
      const along = Math.pow(Math.max(0, 1 - Math.abs(fx)), 0.7);
      const a = Math.min(1, (core + halo) * along);
      const i = (y * T + x) * 4;
      img.data[i] = 255; img.data[i + 1] = 255; img.data[i + 2] = 255; img.data[i + 3] = Math.round(a * 255);
    }
  }
  ctx.putImageData(img, T, 0);

  // tile 2 (smoke) — bottom-left: blotchy soft puff
  ctx.save();
  ctx.translate(0, T);
  const mask = ctx.createRadialGradient(T / 2, T / 2, T * 0.1, T / 2, T / 2, T / 2);
  mask.addColorStop(0, 'rgba(255,255,255,0.9)');
  mask.addColorStop(0.55, 'rgba(255,255,255,0.45)');
  mask.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = mask; ctx.fillRect(0, 0, T, T);
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 26; i++) {
    const r = 12 + Math.random() * 40;
    const x = T / 2 + (Math.random() - 0.5) * T * 0.8, y = T / 2 + (Math.random() - 0.5) * T * 0.8;
    const rg = ctx.createRadialGradient(x, y, 0, x, y, r);
    rg.addColorStop(0, `rgba(0,0,0,${0.25 + Math.random() * 0.35})`);
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg; ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  ctx.restore();

  // tile 3 (disc) — bottom-right: solid soft-edged disc (rail discs, shells)
  g = ctx.createRadialGradient(T * 1.5, T * 1.5, 0, T * 1.5, T * 1.5, T / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.85, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g; ctx.fillRect(T, T, T, T);

  const tex = new THREE.CanvasTexture(c);
  tex.flipY = false;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

const VERT = /* glsl */`
  attribute vec3 aPos;
  attribute float aSize;
  attribute vec4 aColor;
  attribute float aRot;
  attribute vec3 aVel;
  attribute float aStretch;
  attribute float aTex;
  varying vec4 vColor;
  varying vec2 vUv;
  void main() {
    vColor = aColor;
    vec2 tileOff = vec2(mod(aTex, 2.0), floor(aTex / 2.0)) * 0.5;
    vUv = uv * 0.5 + tileOff;
    vec4 mv = viewMatrix * vec4(aPos, 1.0);
    vec2 dir;
    if (aStretch > 0.0) {
      vec3 vv = (viewMatrix * vec4(aVel, 0.0)).xyz;
      dir = length(vv.xy) > 1e-5 ? normalize(vv.xy) : vec2(1.0, 0.0);
    } else {
      dir = vec2(cos(aRot), sin(aRot));
    }
    vec2 p = dir * (position.x * aSize * (1.0 + aStretch)) + vec2(-dir.y, dir.x) * (position.y * aSize);
    mv.xy += p;
    gl_Position = projectionMatrix * mv;
  }
`;
const FRAG = /* glsl */`
  uniform sampler2D map;
  varying vec4 vColor;
  varying vec2 vUv;
  void main() {
    vec4 t = texture2D(map, vUv);
    float a = vColor.a * t.a;
    if (a < 0.003) discard;
    gl_FragColor = vec4(vColor.rgb * t.rgb, a);
  }
`;

/** One pooled billboard system (fixed capacity, free-list). */
class BillboardPool {
  readonly mesh: THREE.InstancedMesh;
  private readonly n: number;
  private pos: Float32Array; private size: Float32Array; private color: Float32Array; private rot: Float32Array;
  private vel: Float32Array; private stretch: Float32Array; private tex: Float32Array;
  private aPos: THREE.InstancedBufferAttribute; private aSize: THREE.InstancedBufferAttribute; private aColor: THREE.InstancedBufferAttribute;
  private aRot: THREE.InstancedBufferAttribute; private aVel: THREE.InstancedBufferAttribute; private aStretch: THREE.InstancedBufferAttribute; private aTex: THREE.InstancedBufferAttribute;
  // simulation state
  private life: Float32Array; private maxLife: Float32Array; private alpha0: Float32Array; private grow: Float32Array;
  private drag: Float32Array; private grav: Float32Array; private rotVel: Float32Array; private size0: Float32Array; private fadeIn: Float32Array;
  private free: number[] = [];
  private active: number[] = [];
  private dirty = false;
  private tmpColor = new THREE.Color();

  constructor(atlas: THREE.Texture, n: number, blending: THREE.Blending) {
    this.n = n;
    const geo = new THREE.PlaneGeometry(1, 1);
    this.pos = new Float32Array(n * 3); this.size = new Float32Array(n); this.color = new Float32Array(n * 4); this.rot = new Float32Array(n);
    this.vel = new Float32Array(n * 3); this.stretch = new Float32Array(n); this.tex = new Float32Array(n);
    this.life = new Float32Array(n); this.maxLife = new Float32Array(n); this.alpha0 = new Float32Array(n); this.grow = new Float32Array(n);
    this.drag = new Float32Array(n); this.grav = new Float32Array(n); this.rotVel = new Float32Array(n); this.size0 = new Float32Array(n); this.fadeIn = new Float32Array(n);
    geo.setAttribute('aPos', this.aPos = new THREE.InstancedBufferAttribute(this.pos, 3));
    geo.setAttribute('aSize', this.aSize = new THREE.InstancedBufferAttribute(this.size, 1));
    geo.setAttribute('aColor', this.aColor = new THREE.InstancedBufferAttribute(this.color, 4));
    geo.setAttribute('aRot', this.aRot = new THREE.InstancedBufferAttribute(this.rot, 1));
    geo.setAttribute('aVel', this.aVel = new THREE.InstancedBufferAttribute(this.vel, 3));
    geo.setAttribute('aStretch', this.aStretch = new THREE.InstancedBufferAttribute(this.stretch, 1));
    geo.setAttribute('aTex', this.aTex = new THREE.InstancedBufferAttribute(this.tex, 1));
    for (const a of [this.aPos, this.aSize, this.aColor, this.aRot, this.aVel, this.aStretch, this.aTex]) a.setUsage(THREE.DynamicDrawUsage);
    const mat = new THREE.ShaderMaterial({
      uniforms: { map: { value: atlas } },
      vertexShader: VERT, fragmentShader: FRAG,
      transparent: true, depthWrite: false, depthTest: true, blending,
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, n);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = blending === THREE.AdditiveBlending ? 20 : 10;
    for (let i = n - 1; i >= 0; i--) this.free.push(i);
  }

  spawn(p: {
    x: number; y: number; z: number; vx?: number; vy?: number; vz?: number;
    size: number; color: number | THREE.Color; alpha?: number; life: number; tex: number;
    grow?: number; drag?: number; gravity?: number; stretch?: number; rot?: number; rotVel?: number; fadeIn?: number;
  }): number {
    let i: number;
    if (this.free.length) i = this.free.pop()!;
    else {
      // Pool plein : on vole la plus ancienne particule COURTE. Les motes
      // (vie > 5 s) sont protégées, sinon le champ d'ambiance se vide en combat.
      let k = -1;
      for (let j = 0; j < this.active.length; j++) {
        if (this.maxLife[this.active[j]] <= 5) { k = j; break; }
      }
      if (k < 0) return -1; // rien de volable : on saute ce spawn
      i = this.active.splice(k, 1)[0];
    }
    this.active.push(i);
    this.pos[i * 3] = p.x; this.pos[i * 3 + 1] = p.y; this.pos[i * 3 + 2] = p.z;
    this.vel[i * 3] = p.vx ?? 0; this.vel[i * 3 + 1] = p.vy ?? 0; this.vel[i * 3 + 2] = p.vz ?? 0;
    this.size0[i] = p.size; this.size[i] = p.size;
    const c = typeof p.color === 'number' ? this.tmpColor.setHex(p.color) : p.color;
    this.color[i * 4] = c.r; this.color[i * 4 + 1] = c.g; this.color[i * 4 + 2] = c.b; this.color[i * 4 + 3] = p.alpha ?? 1;
    this.alpha0[i] = p.alpha ?? 1;
    this.life[i] = p.life; this.maxLife[i] = p.life;
    this.tex[i] = p.tex; this.grow[i] = p.grow ?? 0; this.drag[i] = p.drag ?? 0; this.grav[i] = p.gravity ?? 0;
    this.stretch[i] = p.stretch ?? 0; this.rot[i] = p.rot ?? Math.random() * Math.PI * 2; this.rotVel[i] = p.rotVel ?? 0;
    this.fadeIn[i] = p.fadeIn ?? 0;
    return i;
  }

  update(dt: number): void {
    const act = this.active;
    let w = 0;
    for (let k = 0; k < act.length; k++) {
      const i = act[k];
      const l = this.life[i] - dt;
      if (l <= 0) { this.color[i * 4 + 3] = 0; this.free.push(i); continue; }
      this.life[i] = l;
      act[w++] = i;
      const t = 1 - l / this.maxLife[i];
      // integrate
      const d = this.drag[i] > 0 ? Math.max(0, 1 - this.drag[i] * dt) : 1;
      this.vel[i * 3] *= d; this.vel[i * 3 + 2] *= d;
      this.vel[i * 3 + 1] = this.vel[i * 3 + 1] * d - this.grav[i] * dt;
      this.pos[i * 3] += this.vel[i * 3] * dt; this.pos[i * 3 + 1] += this.vel[i * 3 + 1] * dt; this.pos[i * 3 + 2] += this.vel[i * 3 + 2] * dt;
      this.size[i] = this.size0[i] * (1 + this.grow[i] * t);
      this.rot[i] += this.rotVel[i] * dt;
      // alpha envelope: quick fade-in (optional) then smooth fade-out
      let a = 1 - t;
      a = a * a * (3 - 2 * a); // smoothstep
      if (this.fadeIn[i] > 0) { const fi = Math.min(1, (this.maxLife[i] - l) / this.fadeIn[i]); a *= fi; }
      this.color[i * 4 + 3] = this.alpha0[i] * a;
      if (this.stretch[i] > 0) {
        // stretch follows speed (0.06 m/s → tiny, 20 m/s → long)
        const sp = Math.hypot(this.vel[i * 3], this.vel[i * 3 + 1], this.vel[i * 3 + 2]);
        this.stretch[i] = Math.max(0.05, Math.min(6, sp * 0.22));
      }
    }
    act.length = w;
    // rien de vivant → inutile de re-téléverser les 7 attributs chaque frame
    if (w === 0 && !this.dirty) return;
    this.dirty = w > 0;
    this.aPos.needsUpdate = true; this.aSize.needsUpdate = true; this.aColor.needsUpdate = true; this.aRot.needsUpdate = true;
    this.aVel.needsUpdate = true; this.aStretch.needsUpdate = true; this.aTex.needsUpdate = true;
  }

}

export interface LightHandle { set(pos: THREE.Vector3, intensity?: number): void; release(): void; }

/** Vecteur « haut » constant, partagé (aucune allocation par effet). */
const UP = new THREE.Vector3(0, 1, 0);
const _railP1 = new THREE.Vector3(), _railP2 = new THREE.Vector3();

export class FX {
  private scene: THREE.Scene;
  private atlas: THREE.CanvasTexture;
  private add: BillboardPool;
  private smokeP: BillboardPool;
  // debris
  private debris: THREE.InstancedMesh;
  private dPos: Float32Array; private dVel: Float32Array; private dRot: Float32Array; private dRotV: Float32Array; private dScale: Float32Array; private dLife: Float32Array;
  private dFree: number[] = []; private dActive: number[] = [];
  private dObj = new THREE.Object3D();
  private readonly DN = 96;
  // rings
  private rings: Array<{ mesh: THREE.Mesh; life: number; max: number; r0: number; r1: number }> = [];
  // lights
  private lights: Array<{ light: THREE.PointLight; t: number; dur: number; peak: number; held: boolean }> = [];
  private floorQuery: FloorQuery | null = null;
  private tmp = new THREE.Vector3(); private tmp2 = new THREE.Vector3(); private tmp3 = new THREE.Vector3();
  private col = new THREE.Color();
  private motes: number[] = [];
  private moteCenter = new THREE.Vector3(); private moteRadius = 40;
  private moteAcc = 0;
  /** traînées de railgun vivantes — pilotées par `update(dt)`, pas par un rAF */
  private trails: Array<{ objs: THREE.Object3D[]; mats: THREE.Material[]; life: number }> = [];
  /** budget de débris simultanés (réduit sur tactile) */
  private debrisBudget: number;

  constructor(scene: THREE.Scene, opts: { touch?: boolean } = {}) {
    this.debrisBudget = opts.touch ? 32 : 96;
    this.scene = scene;
    this.atlas = buildAtlas();
    this.add = new BillboardPool(this.atlas, 768, THREE.AdditiveBlending);
    this.smokeP = new BillboardPool(this.atlas, 192, THREE.NormalBlending);
    scene.add(this.add.mesh, this.smokeP.mesh);

    // Debris pool
    const dgeo = new THREE.BoxGeometry(1, 0.55, 0.7);
    const dmat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.45, metalness: 0.9 });
    this.debris = new THREE.InstancedMesh(dgeo, dmat, this.DN);
    this.debris.frustumCulled = false;
    this.debris.castShadow = false;
    this.dPos = new Float32Array(this.DN * 3); this.dVel = new Float32Array(this.DN * 3); this.dRot = new Float32Array(this.DN * 3); this.dRotV = new Float32Array(this.DN * 3);
    this.dScale = new Float32Array(this.DN); this.dLife = new Float32Array(this.DN);
    for (let i = 0; i < this.DN; i++) { this.dFree.push(i); this.dObj.scale.setScalar(0); this.dObj.updateMatrix(); this.debris.setMatrixAt(i, this.dObj.matrix); this.debris.setColorAt(i, this.col.setHex(0x333333)); }
    this.debris.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    scene.add(this.debris);

    // Rings
    for (let i = 0; i < 12; i++) {
      const m = new THREE.Mesh(new THREE.RingGeometry(0.72, 1.0, 56), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
      m.visible = false; m.renderOrder = 15;
      scene.add(m);
      this.rings.push({ mesh: m, life: 0, max: 1, r0: 0, r1: 1 });
    }

    // Light pool (constant light count → no shader recompiles)
    for (let i = 0; i < 6; i++) {
      const l = new THREE.PointLight(0xffffff, 0, 10, 2);
      l.position.set(0, -999, 0);
      scene.add(l);
      this.lights.push({ light: l, t: 0, dur: 1, peak: 0, held: false });
    }
  }

  setFloorQuery(q: FloorQuery): void { this.floorQuery = q; }

  // ---------------------------------------------------------------- primitives

  glow(p: THREE.Vector3, color: number, size: number, life: number, opts: { grow?: number; alpha?: number; vx?: number; vy?: number; vz?: number } = {}): number {
    return this.add.spawn({ x: p.x, y: p.y, z: p.z, size, color, life, tex: ATLAS.glow, grow: opts.grow ?? 1.5, alpha: opts.alpha ?? 1, vx: opts.vx, vy: opts.vy, vz: opts.vz });
  }

  sparks(p: THREE.Vector3, dir: THREE.Vector3, color: number, count: number, opts: { speed?: number; spread?: number; life?: number; size?: number; gravity?: number; drag?: number } = {}): void {
    const speed = opts.speed ?? 8, spread = opts.spread ?? 0.9, life = opts.life ?? 0.55, size = opts.size ?? 0.07, grav = opts.gravity ?? 16;
    for (let i = 0; i < count; i++) {
      const v = this.tmp.set((Math.random() - 0.5) * 2 * spread, (Math.random() - 0.5) * 2 * spread, (Math.random() - 0.5) * 2 * spread).add(dir).normalize().multiplyScalar(speed * (0.45 + Math.random() * 0.9));
      this.add.spawn({ x: p.x, y: p.y, z: p.z, vx: v.x, vy: v.y, vz: v.z, size: size * (0.7 + Math.random() * 0.7), color, life: life * (0.6 + Math.random() * 0.8), tex: ATLAS.spark, stretch: 1, gravity: grav, drag: opts.drag ?? 0.6, alpha: 1 });
    }
  }

  smoke(p: THREE.Vector3, v: THREE.Vector3, size: number, life: number, color = 0x3a3a3f, alpha = 0.5, grow = 1.6): number {
    return this.smokeP.spawn({ x: p.x, y: p.y, z: p.z, vx: v.x, vy: v.y, vz: v.z, size, color, alpha, life, tex: ATLAS.smoke, grow, drag: 1.2, gravity: -0.6, rotVel: (Math.random() - 0.5) * 1.2, fadeIn: 0.08 });
  }

  ring(p: THREE.Vector3, normal: THREE.Vector3, color: number, r0: number, r1: number, life: number): void {
    let best = this.rings[0];
    for (const r of this.rings) { if (r.life <= 0) { best = r; break; } if (r.life < best.life) best = r; }
    best.life = life; best.max = life; best.r0 = r0; best.r1 = r1;
    best.mesh.position.copy(p);
    this.tmp.copy(normal).normalize();
    best.mesh.lookAt(this.tmp2.copy(p).add(this.tmp));
    (best.mesh.material as THREE.MeshBasicMaterial).color.setHex(color);
    best.mesh.visible = true;
    best.mesh.scale.setScalar(r0);
  }

  debrisBurst(p: THREE.Vector3, count: number, color: number, speed = 9, size = 0.2): void {
    for (let k = 0; k < count; k++) {
      if (this.dActive.length >= this.debrisBudget) this.dFree.push(this.dActive.shift()!);
      if (!this.dFree.length) this.dFree.push(this.dActive.shift()!);
      const i = this.dFree.pop()!;
      this.dActive.push(i);
      this.dPos[i * 3] = p.x; this.dPos[i * 3 + 1] = p.y; this.dPos[i * 3 + 2] = p.z;
      const v = this.tmp.set(Math.random() - 0.5, Math.random() * 0.9 + 0.15, Math.random() - 0.5).normalize().multiplyScalar(speed * (0.4 + Math.random()));
      this.dVel[i * 3] = v.x; this.dVel[i * 3 + 1] = v.y; this.dVel[i * 3 + 2] = v.z;
      this.dRot[i * 3] = Math.random() * 6; this.dRot[i * 3 + 1] = Math.random() * 6; this.dRot[i * 3 + 2] = Math.random() * 6;
      this.dRotV[i * 3] = (Math.random() - 0.5) * 18; this.dRotV[i * 3 + 1] = (Math.random() - 0.5) * 18; this.dRotV[i * 3 + 2] = (Math.random() - 0.5) * 18;
      this.dScale[i] = size * (0.5 + Math.random());
      this.dLife[i] = 2.2 + Math.random() * 1.6;
      this.col.setHex(color);
      if (Math.random() < 0.5) this.col.multiplyScalar(0.25 + Math.random() * 0.35); // half the chunks are dark metal
      this.debris.setColorAt(i, this.col);
    }
    if (this.debris.instanceColor) this.debris.instanceColor.needsUpdate = true;
  }

  /** Brass casing ejected to the right of the weapon. */
  casing(p: THREE.Vector3, right: THREE.Vector3, up: THREE.Vector3): void {
    if (this.dActive.length >= this.debrisBudget) this.dFree.push(this.dActive.shift()!);
    if (!this.dFree.length) this.dFree.push(this.dActive.shift()!);
    const i = this.dFree.pop()!;
    this.dActive.push(i);
    this.dPos[i * 3] = p.x; this.dPos[i * 3 + 1] = p.y; this.dPos[i * 3 + 2] = p.z;
    const v = this.tmp.copy(right).multiplyScalar(2.2 + Math.random()).addScaledVector(up, 1.6 + Math.random() * 0.8);
    this.dVel[i * 3] = v.x; this.dVel[i * 3 + 1] = v.y; this.dVel[i * 3 + 2] = v.z;
    this.dRot[i * 3] = 0; this.dRot[i * 3 + 1] = Math.random() * 6; this.dRot[i * 3 + 2] = 0;
    this.dRotV[i * 3] = 20; this.dRotV[i * 3 + 1] = 4; this.dRotV[i * 3 + 2] = 14;
    this.dScale[i] = 0.055;
    this.dLife[i] = 2.0;
    this.debris.setColorAt(i, this.col.setHex(0xd9a441));
    if (this.debris.instanceColor) this.debris.instanceColor.needsUpdate = true;
  }

  /** One-shot light flash (decays over `life`). */
  light(p: THREE.Vector3, color: number, intensity: number, distance: number, life: number): void {
    let slot = this.lights.find(s => !s.held && s.t >= s.dur) ?? null;
    if (!slot) { // steal the dimmest non-held
      let dim = Infinity;
      for (const s of this.lights) { if (s.held) continue; const cur = s.peak * (1 - s.t / s.dur); if (cur < dim) { dim = cur; slot = s; } }
    }
    if (!slot) return;
    slot.t = 0; slot.dur = life; slot.peak = intensity;
    slot.light.color.setHex(color); slot.light.distance = distance; slot.light.position.copy(p); slot.light.intensity = intensity;
  }

  /** Continuous light (rocket engine, LG impact) — returns a handle; release when done. */
  lightHandle(color: number, distance: number): LightHandle | null {
    const slot = this.lights.find(s => !s.held && s.t >= s.dur) ?? null;
    if (!slot) return null;
    slot.held = true; slot.peak = 0; slot.t = 0; slot.dur = 1;
    slot.light.color.setHex(color); slot.light.distance = distance; slot.light.intensity = 0;
    return {
      set: (pos, intensity = slot.light.intensity) => { slot.light.position.copy(pos); slot.light.intensity = intensity; },
      release: () => { slot.held = false; slot.t = slot.dur; slot.light.intensity = 0; slot.light.position.set(0, -999, 0); },
    };
  }

  // ---------------------------------------------------------------- composed effects

  muzzleFlash(p: THREE.Vector3, dir: THREE.Vector3, kind: 'lightning' | 'rocket' | 'rail' | 'shotgun', right: THREE.Vector3, up: THREE.Vector3): void {
    switch (kind) {
      case 'lightning':
        this.glow(p, 0x9be7ff, 0.28 + Math.random() * 0.12, 0.06, { grow: 0.8 });
        this.light(p, 0x9be7ff, 25, 6, 0.08);
        break;
      case 'rocket':
        this.glow(p, 0xffffff, 0.45, 0.07, { grow: 1.2 });
        this.glow(p, 0xff8a30, 0.9, 0.13, { grow: 1.8 });
        this.sparks(p, dir, 0xffb060, 10, { speed: 12, spread: 0.45, life: 0.35, size: 0.06 });
        for (let i = 0; i < 4; i++) this.smoke(this.tmp2.copy(p).addScaledVector(dir, 0.2 + i * 0.25), this.tmp.copy(dir).multiplyScalar(2.5 + i).addScaledVector(up, 0.6), 0.35, 1.1 + Math.random() * 0.5, 0x4a4540, 0.42, 2.2);
        this.light(p, 0xff8a30, 90, 12, 0.16);
        break;
      case 'rail':
        this.glow(p, 0xffffff, 0.35, 0.08, { grow: 1.0 });
        this.glow(p, 0x2fff9a, 0.6, 0.14, { grow: 2.2 });
        this.ring(p, dir, 0x2fff9a, 0.15, 0.9, 0.25);
        this.light(p, 0x2fff9a, 70, 10, 0.16);
        break;
      case 'shotgun':
        this.glow(p, 0xfff2cc, 0.55, 0.06, { grow: 1.4 });
        this.glow(p, 0xffb040, 0.75, 0.1, { grow: 1.6 });
        this.sparks(p, dir, 0xffd080, 16, { speed: 16, spread: 0.35, life: 0.28, size: 0.05 });
        for (let i = 0; i < 3; i++) this.smoke(this.tmp2.copy(p).addScaledVector(dir, 0.3 + i * 0.3), this.tmp.copy(dir).multiplyScalar(3 + i * 1.5), 0.3, 0.9, 0x555048, 0.35, 2.0);
        this.light(p, 0xffc070, 80, 10, 0.1);
        this.casing(this.tmp2.copy(p).addScaledVector(dir, -0.5).addScaledVector(right, 0.1), right, up);
        break;
    }
  }

  /** Impact of a hitscan/beam on the world. */
  impact(p: THREE.Vector3, normal: THREE.Vector3, color: number, strength: number): void {
    const q = this.tmp2.copy(p).addScaledVector(normal, 0.03);
    if (strength >= 3) { // rail
      this.glow(q, 0xffffff, 0.5, 0.1, { grow: 1.5 });
      this.glow(q, color, 1.1, 0.35, { grow: 2.0 });
      this.sparks(q, normal, color, 28, { speed: 12, spread: 0.8, life: 0.7, size: 0.08 });
      this.sparks(q, normal, 0xffffff, 8, { speed: 6, spread: 1.0, life: 0.4, size: 0.05 });
      this.ring(q, normal, color, 0.1, 1.4, 0.4);
      this.smoke(q, this.tmp.copy(normal).multiplyScalar(1.5), 0.5, 1.4, 0x3d3a44, 0.4, 2.4);
      this.light(q, color, 60, 9, 0.3);
      this.debrisBurst(q, 5, 0x555a66, 5, 0.09);
    } else if (strength >= 2) { // shotgun pellet cluster
      this.glow(q, color, 0.18, 0.07, { grow: 1.0 });
      this.sparks(q, normal, color, 4, { speed: 7, spread: 0.9, life: 0.35, size: 0.045 });
    } else { // lightning tick
      this.glow(q, color, 0.22 + Math.random() * 0.15, 0.07, { grow: 1.2 });
      if (Math.random() < 0.55) this.sparks(q, normal, color, 3, { speed: 6, spread: 1.0, life: 0.3, size: 0.045, gravity: 10 });
    }
  }

  /** Flesh hit (bot/player) — accent-colored energy sparks. */
  hitFlesh(p: THREE.Vector3, dir: THREE.Vector3, accent: number, amount: number): void {
    const n = Math.min(24, 4 + Math.round(amount * 0.35));
    this.glow(p, accent, 0.3 + amount * 0.01, 0.12, { grow: 1.5 });
    // tmp3 : `sparks` utilise tmp en interne, passer tmp ici s'auto-additionnait
    const back = this.tmp3.copy(dir).multiplyScalar(-0.3);
    back.y += 0.4;
    this.sparks(p, back, accent, n, { speed: 5, spread: 1.0, life: 0.45, size: 0.05, gravity: 12 });
  }

  explosion(p: THREE.Vector3, radius: number, color = 0xff7a00, normal?: THREE.Vector3): void {
    this.glow(p, 0xffffff, radius * 0.35, 0.16, { grow: 3.2 });
    this.glow(p, 0xffd080, radius * 0.55, 0.32, { grow: 2.6 });
    this.glow(p, color, radius * 0.7, 0.5, { grow: 2.2, alpha: 0.9 });
    this.sparks(p, UP, 0xffc070, 34, { speed: 15, spread: 1.6, life: 0.8, size: 0.09, gravity: 20 });
    this.sparks(p, UP, color, 20, { speed: 9, spread: 1.6, life: 0.6, size: 0.07, gravity: 14 });
    for (let i = 0; i < 12; i++) {
      const v = this.tmp.set(Math.random() - 0.5, Math.random() * 0.7 + 0.2, Math.random() - 0.5).normalize().multiplyScalar(3 + Math.random() * 4);
      this.smoke(this.tmp2.copy(p).addScaledVector(v, 0.12), v, radius * 0.32, 1.7 + Math.random() * 1.1, i % 3 === 0 ? 0x5a3a24 : 0x2a2a2e, 0.55, 1.9);
    }
    this.ring(p, normal ?? UP, 0xffb060, 0.3, radius * 1.5, 0.42);
    this.debrisBurst(p, 10, 0x6a6f7a, 10, 0.16);
    this.light(p, 0xff9a40, 140, radius * 5, 0.45);
  }

  /** Legend death: accent-colored energy burst + armor debris. */
  deathBurst(p: THREE.Vector3, accent: number): void {
    this.glow(p, 0xffffff, 0.8, 0.14, { grow: 2.5 });
    this.glow(p, accent, 1.4, 0.4, { grow: 2.4 });
    this.sparks(p, UP, accent, 36, { speed: 9, spread: 1.6, life: 0.9, size: 0.08, gravity: 14 });
    this.sparks(p, UP, 0xffffff, 10, { speed: 5, spread: 1.6, life: 0.5, size: 0.05 });
    for (let i = 0; i < 6; i++) {
      const v = this.tmp.set(Math.random() - 0.5, Math.random() * 0.6 + 0.3, Math.random() - 0.5).normalize().multiplyScalar(2 + Math.random() * 2);
      this.smoke(p, v, 0.55, 1.6 + Math.random(), 0x25252c, 0.45, 1.8);
    }
    this.ring(p, UP, accent, 0.2, 3.2, 0.5);
    this.debrisBurst(p, 16, accent, 8, 0.18);
    this.light(p, accent, 80, 12, 0.5);
  }

  /** Railgun trail: white core beam + spiral + glowing discs along the path. */
  railTrail(a: THREE.Vector3, b: THREE.Vector3, color: number): void {
    const dir = this.tmp.subVectors(b, a); const len = dir.length(); dir.normalize();
    const coreMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false });
    const haloMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false });
    const spiralMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false });
    const core = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, len, 6, 1, true), coreMat);
    core.position.copy(a).addScaledVector(dir, len / 2);
    core.quaternion.setFromUnitVectors(UP, dir);
    const halo = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, len, 8, 1, true), haloMat);
    halo.position.copy(core.position); halo.quaternion.copy(core.quaternion);
    const up = Math.abs(dir.y) < 0.9 ? this.tmp2.set(0, 1, 0) : this.tmp2.set(1, 0, 0);
    const p1 = _railP1.crossVectors(dir, up).normalize();
    const p2 = _railP2.crossVectors(dir, p1).normalize();
    // buffer rempli directement (plus de 400 Vector3 jetables par tir)
    const turns = len * 1.1;
    const N = Math.min(120, Math.max(40, Math.round(len * 6)));
    const buf = new Float32Array((N + 1) * 3);
    for (let i = 0; i <= N; i++) {
      const t = i / N; const ang = t * turns * Math.PI * 2;
      const c = Math.cos(ang) * 0.2, sn = Math.sin(ang) * 0.2;
      buf[i * 3] = a.x + dir.x * len * t + p1.x * c + p2.x * sn;
      buf[i * 3 + 1] = a.y + dir.y * len * t + p1.y * c + p2.y * sn;
      buf[i * 3 + 2] = a.z + dir.z * len * t + p1.z * c + p2.z * sn;
    }
    const sgeo = new THREE.BufferGeometry();
    sgeo.setAttribute('position', new THREE.BufferAttribute(buf, 3));
    const spiral = new THREE.Line(sgeo, spiralMat);
    this.scene.add(core, halo, spiral);
    // glowing discs along the beam (the classic rail look)
    const step = 0.55;
    for (let d = 0.3; d < len; d += step) {
      const q = this.tmp3.copy(a).addScaledVector(dir, d);
      this.add.spawn({ x: q.x, y: q.y, z: q.z, size: 0.22, color, alpha: 0.9, life: 0.7 + (d / len) * 0.35, tex: ATLAS.disc, grow: 1.4, rotVel: 0 });
    }
    // le fondu est piloté par update(dt) : indépendant du framerate, et les
    // trois matériaux sont disposés à la fin (plus de fuite GPU par tir).
    this.trails.push({ objs: [core, halo, spiral], mats: [coreMat, haloMat, spiralMat], life: 1 });
  }

  /** Fondu + nettoyage des traînées de rail (appelé par `update`). */
  private updateTrails(dt: number): void {
    for (let i = this.trails.length - 1; i >= 0; i--) {
      const tr = this.trails[i];
      tr.life -= dt * 1.5; // ~0,67 s de fondu, quel que soit le framerate
      const l = Math.max(0, tr.life);
      (tr.mats[0] as THREE.MeshBasicMaterial).opacity = l;
      (tr.mats[1] as THREE.MeshBasicMaterial).opacity = l * 0.55;
      (tr.mats[2] as THREE.LineBasicMaterial).opacity = l;
      if (tr.life <= 0) {
        for (const o of tr.objs) {
          this.scene.remove(o);
          ((o as THREE.Mesh).geometry as THREE.BufferGeometry).dispose();
        }
        for (const m of tr.mats) m.dispose();
        this.trails.splice(i, 1);
      }
    }
  }

  /** Brief pellet tracer along a ray. */
  tracer(a: THREE.Vector3, dir: THREE.Vector3, len: number, color: number): void {
    const n = len > 12 ? 3 : 2;
    for (let i = 1; i <= n; i++) {
      const d = (i / (n + 1)) * Math.min(len, 40);
      const q = this.tmp2.copy(a).addScaledVector(dir, d);
      this.add.spawn({ x: q.x, y: q.y, z: q.z, vx: dir.x * 90, vy: dir.y * 90, vz: dir.z * 90, size: 0.05, color, alpha: 0.8, life: 0.07, tex: ATLAS.spark, stretch: 1 });
    }
  }

  /** Rocket engine: flame glow + smoke puff (call every frame while flying). */
  rocketTrail(p: THREE.Vector3, back: THREE.Vector3, dt: number, acc: { t: number }): void {
    this.glow(p, 0xffb060, 0.34 + Math.random() * 0.1, 0.06, { grow: 0.6 });
    this.glow(this.tmp2.copy(p).addScaledVector(back, 0.35), 0xff6a20, 0.5, 0.1, { grow: 1.0, alpha: 0.8 });
    acc.t += dt;
    if (acc.t > 0.022) {
      acc.t = 0;
      const v = this.tmp.copy(back).multiplyScalar(1.5 + Math.random());
      v.x += (Math.random() - 0.5) * 0.6; v.y += 0.4; v.z += (Math.random() - 0.5) * 0.6;
      this.smoke(this.tmp2.copy(p).addScaledVector(back, 0.4), v, 0.28, 1.5 + Math.random() * 0.6, 0x8a8a90, 0.38, 2.6);
      if (Math.random() < 0.3) this.sparks(p, back, 0xffc080, 1, { speed: 3, spread: 0.5, life: 0.3, size: 0.04, gravity: 6 });
    }
  }

  /** Teleporter / jump pad usage burst. */
  burst(p: THREE.Vector3, color: number, size = 1): void {
    this.glow(p, color, 0.8 * size, 0.25, { grow: 2.0 });
    this.sparks(p, UP, color, 16, { speed: 6 * size, spread: 1.2, life: 0.5, size: 0.06, gravity: 8 });
    this.ring(p, UP, color, 0.2, 2.2 * size, 0.4);
    this.light(p, color, 40, 8, 0.3);
  }

  /** Ambient dust motes drifting in the arena volume (persistent). */
  ambientMotes(center: THREE.Vector3, radius: number, count: number): void {
    this.moteCenter.copy(center); this.moteRadius = radius;
    for (let i = 0; i < count; i++) this.motes.push(this.spawnMote());
  }
  private spawnMote(): number {
    const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * this.moteRadius;
    const warm = Math.random() < 0.5;
    return this.add.spawn({
      x: this.moteCenter.x + Math.cos(a) * r, y: this.moteCenter.y + (Math.random() - 0.5) * 30, z: this.moteCenter.z + Math.sin(a) * r,
      vx: (Math.random() - 0.5) * 0.35, vy: (Math.random() - 0.5) * 0.25, vz: (Math.random() - 0.5) * 0.35,
      size: 0.035 + Math.random() * 0.045, color: warm ? 0xffc070 : 0x9be7ff, alpha: 0.35 + Math.random() * 0.3, life: 10 + Math.random() * 14, tex: ATLAS.glow, grow: 0, fadeIn: 2,
    });
  }

  // ---------------------------------------------------------------- update

  update(dt: number): void {
    this.add.update(dt);
    this.smokeP.update(dt);
    this.updateTrails(dt);
    // motes : ~6 recyclages/s (90 motes / 17 s de vie moyenne ≈ 5,3/s)
    if (this.motes.length) {
      this.moteAcc += dt * 6;
      while (this.moteAcc >= 1) { this.moteAcc -= 1; this.motes.shift(); this.motes.push(this.spawnMote()); }
    }

    // debris
    const act = this.dActive; let w = 0;
    for (let k = 0; k < act.length; k++) {
      const i = act[k];
      const l = this.dLife[i] - dt;
      if (l <= 0) { this.dObj.scale.setScalar(0); this.dObj.updateMatrix(); this.debris.setMatrixAt(i, this.dObj.matrix); this.dFree.push(i); continue; }
      this.dLife[i] = l; act[w++] = i;
      this.dVel[i * 3 + 1] -= 22 * dt;
      let x = this.dPos[i * 3] + this.dVel[i * 3] * dt, y = this.dPos[i * 3 + 1] + this.dVel[i * 3 + 1] * dt, z = this.dPos[i * 3 + 2] + this.dVel[i * 3 + 2] * dt;
      if (this.floorQuery) {
        const f = this.floorQuery(x, this.dPos[i * 3 + 1], z);
        const h = this.dScale[i] * 0.3;
        if (f !== null && y < f + h && this.dVel[i * 3 + 1] < 0) {
          y = f + h;
          this.dVel[i * 3 + 1] *= -0.38; this.dVel[i * 3] *= 0.7; this.dVel[i * 3 + 2] *= 0.7;
          this.dRotV[i * 3] *= 0.5; this.dRotV[i * 3 + 1] *= 0.5; this.dRotV[i * 3 + 2] *= 0.5;
          if (Math.abs(this.dVel[i * 3 + 1]) < 0.6) { this.dVel[i * 3 + 1] = 0; this.dRotV[i * 3] = 0; this.dRotV[i * 3 + 2] = 0; }
        }
      }
      this.dPos[i * 3] = x; this.dPos[i * 3 + 1] = y; this.dPos[i * 3 + 2] = z;
      this.dRot[i * 3] += this.dRotV[i * 3] * dt; this.dRot[i * 3 + 1] += this.dRotV[i * 3 + 1] * dt; this.dRot[i * 3 + 2] += this.dRotV[i * 3 + 2] * dt;
      const s = this.dScale[i] * (l < 0.5 ? l / 0.5 : 1);
      this.dObj.position.set(x, y, z); this.dObj.rotation.set(this.dRot[i * 3], this.dRot[i * 3 + 1], this.dRot[i * 3 + 2]); this.dObj.scale.setScalar(s);
      this.dObj.updateMatrix(); this.debris.setMatrixAt(i, this.dObj.matrix);
    }
    const hadDebris = act.length > 0;
    act.length = w;
    if (w > 0 || hadDebris) this.debris.instanceMatrix.needsUpdate = true;

    // rings
    for (const r of this.rings) {
      if (r.life <= 0) { if (r.mesh.visible) r.mesh.visible = false; continue; }
      r.life -= dt;
      const t = 1 - Math.max(0, r.life) / r.max;
      const e = 1 - Math.pow(1 - t, 3);
      r.mesh.scale.setScalar(r.r0 + (r.r1 - r.r0) * e);
      (r.mesh.material as THREE.MeshBasicMaterial).opacity = (1 - t) * 0.9;
    }

    // lights
    for (const s of this.lights) {
      if (s.held || s.t >= s.dur) continue;
      s.t += dt;
      const k = Math.max(0, 1 - s.t / s.dur);
      s.light.intensity = s.peak * k * k;
      if (s.t >= s.dur) { s.light.intensity = 0; s.light.position.set(0, -999, 0); }
    }
  }
}
